import React, { useEffect, useMemo, useState } from 'react';
import { apiGet } from '@/context/AuthContext';
import { Loader2, Search, Users } from 'lucide-react';

function initials(name) {
  return (name || 'G')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export default function GuestDirectoryPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    apiGet('/api/guest-directory')
      .then((res) => setProfiles(res.data.profiles || []))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const values = profiles.map((profile) => profile.category).filter(Boolean);
    return ['all', ...Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, 'da'))];
  }, [profiles]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return profiles.filter((profile) => {
      if (category !== 'all' && profile.category !== category) return false;
      if (!needle) return true;
      return [
        profile.full_name,
        profile.relation,
        profile.role,
        profile.title,
        profile.description,
        profile.fun_fact,
        profile.category,
        profile.table_id,
      ].some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [category, profiles, search]);

  return (
    <div className="mx-auto max-w-6xl animate-fade-in" data-testid="guest-directory-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Mød gæsterne</h1>
        <p className="mt-1 text-sm text-slate-400">Se hvem der er med til Molin30. Private kontaktoplysninger vises ikke.</p>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg efter navn, relation, rolle eller bord"
            className="w-full rounded-md border border-slate-700 bg-slate-900/60 py-2 pl-9 pr-3 text-sm text-slate-100 outline-none focus:border-blue-500"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500 sm:w-56"
        >
          {categories.map((item) => <option key={item} value={item}>{item === 'all' ? 'Alle kategorier' : item}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Indlæser gæster...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 py-16 text-center text-slate-500">
          <Users className="mx-auto mb-3 h-8 w-8 text-slate-600" />
          <p className="text-sm">Ingen gæster matcher søgningen.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((profile) => (
            <article key={profile.id} className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-5">
              <div className="mb-4 flex items-start gap-3">
                {profile.profile_image ? (
                  <img src={profile.profile_image} alt="" className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-sm font-bold text-white">
                    {initials(profile.full_name)}
                  </div>
                )}
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-slate-100">{profile.full_name}</h2>
                  <p className="text-xs text-slate-500">{profile.relation || profile.category || 'Gæst'}</p>
                </div>
              </div>
              {(profile.role || profile.title || profile.table_id) && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {profile.role && <span className="rounded-full bg-purple-500/10 px-2.5 py-1 text-xs text-purple-300">{profile.role}</span>}
                  {profile.title && <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs text-blue-300">{profile.title}</span>}
                  {profile.table_id && <span className="rounded-full bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-300">Bord {profile.table_id}</span>}
                </div>
              )}
              {profile.description && <p className="mb-3 text-sm leading-relaxed text-slate-300">{profile.description}</p>}
              {profile.fun_fact && (
                <p className="rounded-lg bg-slate-900/50 p-3 text-xs leading-relaxed text-slate-400">
                  <span className="font-medium text-slate-300">Fun fact: </span>{profile.fun_fact}
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
